import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, CalendarDays, Clock, MapPin } from "lucide-react";
import { Brand } from "@/components/Brand";
import { DisclosureFooter } from "@/components/FundingDisclosure";
import { usePageMeta } from "@/lib/seo";
import { ShareKit } from "@/components/ShareKit";
import { api } from "@/lib/api";

const T = {
  en: { seat: "Save my free seat", full: "Full · join waitlist", left: "seats left", lang: "In English" },
  es: { seat: "Reservar mi lugar gratis", full: "Lleno · lista de espera", left: "lugares disponibles", lang: "En español" },
};

function WorkshopCard({ w }) {
  const L = T[w.lang];
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5 flex flex-col sm:flex-row sm:items-center gap-4" data-testid={`library-workshop-${w.workshop_id}`}>
      <div className="flex-1 space-y-1.5 text-slate-700">
        {w.title && <p className="font-display font-bold text-slate-900" data-testid={`library-workshop-title-${w.workshop_id}`}>{w.title}</p>}
        <p className="flex items-center gap-2 font-semibold text-slate-900"><CalendarDays className="h-4 w-4 text-emerald-600" />{w.display_date}</p>
        {w.time && <p className="flex items-center gap-2 text-sm"><Clock className="h-4 w-4 text-emerald-600" />{w.time}</p>}
        {w.address && <p className="flex items-center gap-2 text-sm"><MapPin className="h-4 w-4 text-emerald-600" />{w.address}</p>}
        <p className="text-xs text-slate-500">{L.lang}{w.seats_left != null && !w.full ? ` · ${w.seats_left} ${L.left}` : ""}</p>
      </div>
      <Link to={`/workshop/${w.workshop_id}`} className={`inline-flex items-center justify-center gap-2 h-11 px-5 rounded-md text-sm font-semibold transition-colors ${w.full ? "bg-amber-100 text-amber-900 hover:bg-amber-200" : "bg-emerald-600 text-white hover:bg-emerald-700"}`} data-testid={`library-register-${w.workshop_id}`}>
        {w.full ? L.full : L.seat}<ArrowRight className="h-4 w-4" />
      </Link>
    </div>
  );
}

export default function LibraryPage() {
  const { slug } = useParams();
  const [d, setD] = useState(null);
  const [missing, setMissing] = useState(false);
  useEffect(() => { api.get(`/public/workshops/library/${slug}`).then((r) => setD(r.data)).catch(() => setMissing(true)); }, [slug]);
  usePageMeta({ title: `Free grant workshops at ${d?.library || "your library"} | GrantVerify`, path: `/library/${slug}`,
    description: `Free "Finding Grants for Your Business" workshops at ${d?.library || "your local library"}. Learn where grants come from, how to get ready and how to avoid scams.` });
  return (
    <div className="min-h-screen bg-slate-50 flex flex-col" data-testid="library-page">
      <header className="bg-white border-b border-slate-200"><div className="max-w-3xl mx-auto px-4 sm:px-6 py-4"><Brand /></div></header>
      <main className="flex-1 max-w-3xl w-full mx-auto px-4 sm:px-6 py-12">
        {missing && <p data-testid="library-missing">We couldn't find workshops for this library.</p>}
        {d && (
          <>
            <div className="eyebrow mb-2">Free grant workshops · Talleres gratuitos</div>
            <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-900" data-testid="library-name">{d.library}</h1>
            <p className="mt-3 text-slate-600 max-w-2xl">"Finding Grants for Your Business": a free 30-minute talk on where federal, state, county and city grants come from, how to get ready to apply and how to avoid grant scams. Open to small business owners, startups and nonprofits.</p>
            <h2 className="mt-10 mb-4 font-display text-lg font-bold text-slate-900">Upcoming workshops</h2>
            {d.upcoming.length ? <div className="space-y-3" data-testid="library-upcoming">{d.upcoming.map((w) => <WorkshopCard key={w.workshop_id} w={w} />)}</div>
              : <p className="text-slate-600" data-testid="library-none">No upcoming workshops right now. Check back soon, or start a free eligibility check below.</p>}
            <div className="mt-12 bg-slate-900 text-white rounded-lg p-6 flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
              <div><div className="font-display text-xl font-bold">Can't make it?</div><p className="text-slate-300 text-sm mt-1">Find grants you qualify for in about 3 minutes, free.</p></div>
              <Link to="/?utm_source=qr&utm_campaign=qr_share&utm_content=library-page" className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold px-5 py-3 rounded-md transition-colors" data-testid="library-cta">Start free <ArrowRight className="h-4 w-4" /></Link>
            </div>
            <ShareKit library={d.library} slug={d.slug} langs={[...new Set(d.upcoming.map((w) => w.lang))]} />
          </>
        )}
      </main>
      <DisclosureFooter />
    </div>
  );
}
